import { createSlice } from '@reduxjs/toolkit';
import { Organization } from '../types/Organization';
import { Employee } from '../types/Employee';
import { setUserItem } from '../utils/userStorage';

type AssessmentPeriod = {
  id: number;
  name: string;
  organization: number;
  status: number;
  startDateTime: string;
  endDateTime: string;
  briefToken: string | null;
  categories?: any[];
};

export enum UserContextInitializationState {
  NOT_INITIALIZED,
  USER_INITIALIZED,
  ORGANIZATION_INITIALIZED,
  ORGANIZATION_TRANSITIONING,
  ERROR,
}

interface UserContextState {
  initializationState: UserContextInitializationState;
  idToken?: string;
  user?: any;
  currentEmployee: Employee | undefined;
  currentOrganization?: Organization;
  organizations: Organization[];
  mostRecentAssessmentPeriod: AssessmentPeriod | undefined;
  previousAssessmentPeriod?: AssessmentPeriod | undefined;
  selectedAssessmentPeriod?: AssessmentPeriod | undefined;
  assessmentPeriods?: AssessmentPeriod[] | undefined;
  demographicFields?: Record<string, any>;
  employeeRecordFields?: Record<string, any>;
  error: string | null;
  lastFetched: number | null;
  areAnyAssessmentPeriodsClosed: boolean;
  // Persisted team selection from the user (if any)
  selectedTeamId?: number | undefined;
}

const initialState = {
  idToken: undefined,
  user: undefined,
  currentOrganization: undefined,
  currentEmployee: undefined,
  organizations: [],
  mostRecentAssessmentPeriod: undefined,
  previousAssessmentPeriod: undefined,
  selectedAssessmentPeriod: undefined,
  assessmentPeriods: undefined,
  demographicFields: undefined,
  employeeRecordFields: undefined,
  initializationState: UserContextInitializationState.NOT_INITIALIZED,
  error: null,
  lastFetched: null,
  areAnyAssessmentPeriodsClosed: false,
  selectedTeamId: undefined,
} satisfies UserContextState as UserContextState;

export function convertSnakeToCamel<T = any>(obj: T, maxDepth: number = 10, currentDepth: number = 0): T {
  if (currentDepth >= maxDepth || !obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => convertSnakeToCamel(item, maxDepth, currentDepth + 1)) as T;
  }

  return Object.entries(obj).reduce((acc, [key, value]) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    (acc as any)[camelKey] = convertSnakeToCamel(value, maxDepth, currentDepth + 1);
    return acc;
  }, {} as T);
}

const userContextSlice = createSlice({
  name: 'userContext',
  initialState,
  reducers: {
    initializeUserContext(state, action) {
      state.idToken = action.payload.idToken;
      state.user = convertSnakeToCamel(action.payload.account);
      state.organizations = action.payload.organizations ?? [];
      state.initializationState = UserContextInitializationState.USER_INITIALIZED;
    },
    clearUserContext(state) {
      return initialState;
    },
    setAuthenticationError(state, action) {
      state.error = action.payload;
      state.initializationState = UserContextInitializationState.ERROR;
    },
    clearAuthenticationError(state) {
      state.error = null;
      if (state.initializationState === UserContextInitializationState.ERROR) {
        state.initializationState = UserContextInitializationState.NOT_INITIALIZED;
      }
    },
    setSelectedOrganization(state, action) {
      const camelOrg = convertSnakeToCamel(action.payload);
      if (camelOrg?.id) {
        setUserItem('selected_org_id', camelOrg.id.toString());
      }
      state.currentOrganization = {
        ...camelOrg,
        services_enabled: [{ name: 'EDI', enabled: true }],
      };
      state.currentEmployee = camelOrg.employee;
      
      const sortedPeriods = (camelOrg.assessmentPeriods ? [...camelOrg.assessmentPeriods] : []).sort(
        (a, b) => {
          const aDate = new Date(a.startDateTime).getTime();
          const bDate = new Date(b.startDateTime).getTime();
          return bDate - aDate; // Most recent first
        }
      );
      
      state.mostRecentAssessmentPeriod = sortedPeriods[0];
      state.selectedAssessmentPeriod = sortedPeriods[0];
      state.previousAssessmentPeriod = sortedPeriods[1] || undefined;
      state.assessmentPeriods = sortedPeriods;
      
      const leadershipTeamIds = Array.isArray(camelOrg.leadershipTeams)
        ? camelOrg.leadershipTeams.map((team: any) => team.id)
        : [];
      if (state.selectedTeamId != null && !leadershipTeamIds.includes(state.selectedTeamId)) {
        state.selectedTeamId = undefined;
      }
      state.initializationState = UserContextInitializationState.ORGANIZATION_INITIALIZED;
    },
    initializeOrganizationContext(state, action) {
      try {
        const orgData = convertSnakeToCamel(action.payload);
        const selectedOrgFromList = convertSnakeToCamel(
          (state.organizations || []).find((org: any) => Number(org?.id) === Number(orgData?.id))
        ) as any;
        const existingOrg = state.currentOrganization as any;
        const resolvedTimezone =
          orgData?.timeZone ||
          orgData?.timezone ||
          orgData?.time_zone ||
          selectedOrgFromList?.timeZone ||
          selectedOrgFromList?.timezone ||
          selectedOrgFromList?.time_zone ||
          existingOrg?.timeZone ||
          existingOrg?.timezone ||
          existingOrg?.time_zone;

        state.currentOrganization = {
          ...selectedOrgFromList,
          ...orgData,
          ...(resolvedTimezone ? { timeZone: resolvedTimezone } : {}),
          services_enabled: [{ name: 'EDI', enabled: true }],
        };

        state.currentEmployee = orgData.employee;
        
        const sortedPeriods = (orgData.assessmentPeriods ? [...orgData.assessmentPeriods] : []).sort(
          (a, b) => {
            const aDate = new Date(a.startDateTime).getTime();
            const bDate = new Date(b.startDateTime).getTime();
            return bDate - aDate; // Most recent first
          }
        );

        state.assessmentPeriods = sortedPeriods;

        state.mostRecentAssessmentPeriod = sortedPeriods[0];

        const currentSelectedId = state.selectedAssessmentPeriod?.id;
        const existingSelected = currentSelectedId
          ? sortedPeriods.find(period => period.id === currentSelectedId)
          : undefined;

        state.selectedAssessmentPeriod = existingSelected ?? sortedPeriods[0];
        const selectedIndex = state.selectedAssessmentPeriod
          ? sortedPeriods.findIndex(period => period.id === state.selectedAssessmentPeriod!.id)
          : -1;
        state.previousAssessmentPeriod = selectedIndex >= 0 ? sortedPeriods[selectedIndex + 1] : undefined;

        state.demographicFields = orgData.demographicFields;
        state.employeeRecordFields = orgData.employeeRecordFields;

        const leadershipTeamIds = Array.isArray(orgData.leadershipTeams)
          ? orgData.leadershipTeams.map((team: any) => team.id)
          : [];

        if (state.selectedTeamId != null && !leadershipTeamIds.includes(state.selectedTeamId)) {
          state.selectedTeamId = undefined;
        }

        state.initializationState = UserContextInitializationState.ORGANIZATION_INITIALIZED;
        state.error = null;
        state.lastFetched = Date.now();
      } catch (error) {
        state.error =
          error instanceof Error ? error.message : 'Failed to initialize organization context';
        state.initializationState = UserContextInitializationState.ERROR;
      }
    },
    setSelectedAssessmentPeriod(state, action) {
      try {
        // Normalize payload to camelCase to avoid inconsistent key shapes across codepaths
        const selected = action.payload.selectedAssessmentPeriod
          ? convertSnakeToCamel(action.payload.selectedAssessmentPeriod)
          : undefined;

        const periods = action.payload.assessmentPeriods
          ? convertSnakeToCamel(action.payload.assessmentPeriods)
          : state.assessmentPeriods;

        state.selectedAssessmentPeriod = selected ?? undefined;
        state.assessmentPeriods = periods ?? state.assessmentPeriods;

        const sortedPeriods = (state.assessmentPeriods ? [...state.assessmentPeriods] : []).sort(
          (a, b) => {
            const aDate = new Date(a.startDateTime).getTime();
            const bDate = new Date(b.startDateTime).getTime();
            return bDate - aDate;
          }
        );

        if (state.selectedAssessmentPeriod && state.selectedAssessmentPeriod.id !== undefined) {
          const selectedIndex = sortedPeriods.findIndex(
            period => period.id === state.selectedAssessmentPeriod!.id
          );

          if (selectedIndex !== -1) {
            state.previousAssessmentPeriod = sortedPeriods[selectedIndex + 1] || undefined;
          } else {
            state.previousAssessmentPeriod = undefined;
          }
        } else {
          state.previousAssessmentPeriod = undefined;
        }

        state.error = null;
        state.lastFetched = Date.now();
      } catch (error) {
        state.error =
          error instanceof Error ? error.message : 'Failed to set selected assessment period';
        state.initializationState = UserContextInitializationState.ERROR;
      }
    },
    setError(state, action) {
      state.error = action.payload;
      state.initializationState = UserContextInitializationState.ERROR;
    },
    clearError(state) {
      state.error = null;
    },
    setTransitionState(state) {
      state.initializationState = UserContextInitializationState.ORGANIZATION_TRANSITIONING;
      state.error = null;
    },
    updateOrganizations(state, action) {
      state.organizations = action.payload;
    },
    // Track a user-selected team id (used across Culture Hub views)
    setSelectedTeamId(state, action) {
      // allow null to explicitly clear selection
      state.selectedTeamId = typeof action.payload === 'number' ? action.payload : (action.payload ?? undefined);
    },
  },

  selectors: {
    initializationState: state => state.initializationState,
    isAuthenticated: state => !!state.idToken,
    getIdToken: state => state.idToken,
    getUser: state => state.user,
    getOrganizations: state => state.organizations,
    getCurrentOrganization: state => state.currentOrganization,

    getCurrentOrganizationMembers: state =>
      [...(state.currentOrganization?.members || [])].sort((a, b) =>
        (a.firstName || '').localeCompare(b.firstName || '')
      ),

    getCurrentEmployee: state => state.currentEmployee,

    // The team id the user has selected in the UI (if any)
    getSelectedTeamId: state => state.selectedTeamId,

    getCurrentAssessmentPeriod: state => state.mostRecentAssessmentPeriod,
    getPreviousAssessmentPeriod: state => state.previousAssessmentPeriod,
    getSelectedAssessmentPeriod: state => state.selectedAssessmentPeriod,
    getAssessmentPeriods: state => state.assessmentPeriods,
    getDemographicFields: state => state.demographicFields,
    getEmployeeRecordFields: state => state.employeeRecordFields,
    getError: state => state.error,
    getLastFetched: state => state.lastFetched,
    isDataStale: state => {
      if (!state.lastFetched) return true;
      const CACHE_DURATION = 5 * 60 * 1000;
      return Date.now() - state.lastFetched > CACHE_DURATION;
    },
  },
});

export default userContextSlice;
